import { useQuery } from '@tanstack/react-query';

export interface SubgraphToken {
  id: string;
  symbol: string;
}

export interface SubgraphPair {
  id: string;
  reserveUSD: string;
  volumeUSD: string;
  token0: SubgraphToken;
  token1: SubgraphToken;
}

export interface SubgraphPairDayData {
  id: string;
  date: number;
  pair: { id: string };
  reserve0: string;
  reserve1: string;
  reserveUSD: string;
  dailyVolumeToken0: string;
  dailyVolumeToken1: string;
  dailyVolumeUSD: string;
}

export interface PairStatsData {
  pairs: SubgraphPair[];
  pairDayDatas: SubgraphPairDayData[];
}

interface GraphResponse<T> {
  data?: T;
  errors?: Array<{ message: string }>;
}

const PAIR_STATS_QUERY = `
  query PairStats {
    pairs(first: 1000, orderBy: reserveUSD, orderDirection: desc) {
      id
      reserveUSD
      volumeUSD
      token0 { id symbol }
      token1 { id symbol }
    }
    pairDayDatas(first: 1000, orderBy: date, orderDirection: desc) {
      id
      date
      pair { id }
      reserve0
      reserve1
      reserveUSD
      dailyVolumeToken0
      dailyVolumeToken1
      dailyVolumeUSD
    }
  }
`;

async function fetchPairStats(url: string): Promise<PairStatsData> {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ query: PAIR_STATS_QUERY }),
  });

  if (!response.ok) throw new Error(`Subgraph request failed (${response.status})`);

  const payload = (await response.json()) as GraphResponse<PairStatsData>;
  if (payload.errors?.length || !payload.data) {
    throw new Error(payload.errors?.[0]?.message ?? 'Subgraph returned no data');
  }
  return payload.data;
}

export function usePairStats(): { data: PairStatsData | undefined; configured: boolean } {
  const url = import.meta.env.VITE_SUBGRAPH_URL?.trim();
  const query = useQuery({
    queryKey: ['subgraph', 'pair-stats', url],
    queryFn: () => fetchPairStats(url as string),
    enabled: Boolean(url),
    retry: false,
    staleTime: 60_000,
    refetchInterval: 60_000,
  });

  if (!url || query.isError) return { data: undefined, configured: false };
  return { data: query.data, configured: true };
}
